import { useEffect, useState } from "react";
import { Info, Smartphone, UtensilsCrossed, MapPin, MousePointerClick, BookOpen, Store, ArrowDown, Mail, Phone, Globe, CalendarClock } from "lucide-react";
import CsbGate from "@/pages/csb/CsbGate";
import CsbConcept from "@/pages/csb/CsbConcept";
import { Reveal, Overline } from "@/components/Motion";
import { api } from "@/lib/api";

const ICONS = [Smartphone, UtensilsCrossed, MapPin, MousePointerClick, BookOpen, Store];

const CsbPresentation = () => {
  const [token, setToken] = useState(() => sessionStorage.getItem("csb_token") || "");
  const [data, setData] = useState(null);
  const [denied, setDenied] = useState(false);

  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.appendChild(meta);
    document.title = "Private Concept — Chai Sutta Bar Canada | Zilleneal Digital";
    return () => {
      document.head.removeChild(meta);
      document.title = "Zilleneal Digital | Web Design, E-commerce & SEO Studio in Alberta";
    };
  }, []);

  useEffect(() => {
    if (!token) return;
    api
      .get("/csb/content", { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setData(r.data))
      .catch(() => {
        sessionStorage.removeItem("csb_token");
        setToken("");
        setDenied(true);
      });
  }, [token]);

  if (!token || denied) {
    return (
      <CsbGate
        onUnlock={(t) => {
          setDenied(false);
          setToken(t);
        }}
      />
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-midnight flex items-center justify-center" data-testid="csb-loading">
        <div className="w-8 h-8 rounded-full border-2 border-white/15 border-t-brand-cyan animate-spin" />
      </div>
    );
  }

  const explore = () => document.getElementById("csb-concept")?.scrollIntoView({ behavior: "smooth" });
  const { intro, improvements, concept, contact, disclaimer } = data;

  return (
    <div className="min-h-screen bg-midnight text-white" data-testid="csb-presentation">
      {/* disclaimer strip */}
      <div className="bg-surface border-b border-white/10" data-testid="csb-disclaimer">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-3.5 flex items-start gap-3">
          <Info className="w-4 h-4 text-brand-cyan shrink-0 mt-0.5" />
          <p className="text-xs text-white/55 leading-relaxed">
            <span className="font-semibold text-white/75 uppercase tracking-[0.15em] mr-2">Independent Design Concept</span>
            {disclaimer}
          </p>
        </div>
      </div>

      {/* slim header */}
      <div className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <p className="font-sora font-extrabold tracking-tight">
            Zilleneal<span className="text-gradient">®</span>
          </p>
          <span className="text-[10px] uppercase tracking-[0.25em] text-white/40 border border-white/15 rounded-full px-4 py-1.5">
            Private Concept
          </span>
        </div>
      </div>

      {/* presentation intro */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 min-h-[70vh] flex flex-col justify-center py-24" data-testid="csb-intro">
        <Reveal>
          <Overline>{intro.overline}</Overline>
          <h1 className="font-sora font-extrabold tracking-tighter text-4xl sm:text-6xl lg:text-7xl leading-[1.02] mt-8 max-w-4xl">
            {intro.heading_a} <span className="text-gradient">{intro.heading_b}</span>
          </h1>
          <p className="mt-8 max-w-2xl text-white/60 leading-relaxed text-base md:text-lg">{intro.body}</p>
          <button
            onClick={explore}
            data-testid="csb-explore-button"
            className="group mt-12 inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-brand-blue to-brand-purple px-9 py-4 font-semibold transition-transform duration-300 hover:scale-[1.04]"
          >
            {intro.cta}
            <ArrowDown className="w-5 h-5 transition-transform duration-300 group-hover:translate-y-1" />
          </button>
        </Reveal>
      </section>

      {/* key improvements */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24" data-testid="csb-improvements">
        <Reveal>
          <Overline>Key Improvements</Overline>
          <h2 className="font-sora font-extrabold tracking-tighter text-3xl sm:text-5xl mt-6 max-w-2xl leading-[1.05]">
            The thinking behind <span className="text-gradient">the redesign</span>
          </h2>
        </Reveal>
        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {improvements.map((c, i) => {
            const Icon = ICONS[i % ICONS.length];
            return (
              <Reveal key={c.title} delay={(i % 3) * 0.1}>
                <div className="glow-border rounded-2xl border border-white/10 bg-surface p-8 h-full transition-transform duration-500 hover:-translate-y-1.5" data-testid={`csb-improvement-${i}`}>
                  <Icon className="w-7 h-7 text-brand-cyan" strokeWidth={1.5} />
                  <h3 className="font-sora font-bold text-xl tracking-tight mt-6">{c.title}</h3>
                  <p className="text-sm text-white/55 mt-3 leading-relaxed">{c.desc}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* the concept site */}
      <CsbConcept c={concept} />

      {/* contact / CTA */}
      <section className="border-t border-white/10" data-testid="csb-contact">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-28">
          <Reveal>
            <div className="max-w-3xl">
              <Overline>{contact.overline}</Overline>
              <h2 className="font-sora font-extrabold tracking-tighter text-3xl sm:text-5xl mt-6 leading-[1.05]">
                {contact.heading_a} <span className="text-gradient">{contact.heading_b}</span>
              </h2>
              <p className="mt-6 text-white/60 leading-relaxed">{contact.body}</p>
              <p className="mt-8 font-sora font-bold tracking-tight text-lg">{contact.name}</p>
              <p className="text-sm text-white/50">{contact.role}</p>
              <a
                href={`mailto:${contact.email}?subject=Chai%20Sutta%20Bar%20Canada%20—%20Concept%20Discussion`}
                data-testid="csb-schedule-button"
                className="group mt-10 inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-brand-blue to-brand-purple px-9 py-4 font-semibold transition-transform duration-300 hover:scale-[1.04]"
              >
                <CalendarClock className="w-5 h-5" />
                {contact.cta}
              </a>
              <div className="mt-12 flex flex-wrap gap-x-12 gap-y-6 text-sm">
                <div className="flex items-center gap-3" data-testid="csb-contact-email">
                  <Mail className="w-4 h-4 text-brand-cyan" />
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.2em] text-white/35">Email</p>
                    <a href={`mailto:${contact.email}`} className="text-white/75 hover:text-brand-cyan transition-colors duration-300">{contact.email}</a>
                  </div>
                </div>
                <div className="flex items-center gap-3" data-testid="csb-contact-phone">
                  <Phone className="w-4 h-4 text-brand-cyan" />
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.2em] text-white/35">Phone</p>
                    <p className="text-white/50">{contact.phone}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3" data-testid="csb-contact-web">
                  <Globe className="w-4 h-4 text-brand-cyan" />
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.2em] text-white/35">Website</p>
                    <p className="text-white/75">{contact.web}</p>
                  </div>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
        <div className="border-t border-white/10 py-8 text-center">
          <p className="text-xs text-white/30">
            © {new Date().getFullYear()} Zilleneal Digital — Independent design concept. Private and confidential.
          </p>
        </div>
      </section>
    </div>
  );
};

export default CsbPresentation;
